import React from "react";
import { Language, AttachMoney, CorporateFare } from "@mui/icons-material";
import { CountryLogo } from "../../../../components/CountryLogo";
import { SpogCompany } from "../../../../legacy/types/SpogCompany";
import {
  VIEW_BY_OPTIONS,
  REVENUE_BANDS,
  COMPANY_TYPES,
  CompanyTypeKey,
  getRevenueBand,
} from "./constants";

export type ViewByOption =
  (typeof VIEW_BY_OPTIONS)[keyof typeof VIEW_BY_OPTIONS];

export interface SortStrategy {
  sort: (companies: SpogCompany[]) => SpogCompany[];
}

export interface GroupStrategy {
  group: (
    companies: SpogCompany[]
  ) => { key: string; companies: SpogCompany[]; icon: React.ReactNode }[];
  needsGrouping: boolean;
}

export const sortStrategies: Record<ViewByOption, SortStrategy> = {
  [VIEW_BY_OPTIONS.NAME]: {
    sort: (companies) =>
      [...companies].sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      }),
  },
  [VIEW_BY_OPTIONS.COUNTRY]: {
    sort: (companies) =>
      [...companies].sort((a, b) => {
        const countryA = a.locationPrimary?.state?.country?.name || "Unknown";
        const countryB = b.locationPrimary?.state?.country?.name || "Unknown";
        if (countryA < countryB) return -1;
        if (countryA > countryB) return 1;
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      }),
  },
  [VIEW_BY_OPTIONS.REVENUE]: {
    sort: (companies) =>
      [...companies].sort((a, b) => {
        const revenueA = a.revenueRange?.min || 0;
        const revenueB = b.revenueRange?.min || 0;
        // Descending order (higher revenue first)
        if (revenueA > revenueB) return -1;
        if (revenueA < revenueB) return 1;
        return 0;
      }),
  },
  [VIEW_BY_OPTIONS.COMPANY_TYPE]: {
    sort: (companies) =>
      [...companies].sort((a, b) => {
        // First, sort by company type priority (isGhq, isGp, isDp)
        for (const companyType of COMPANY_TYPES) {
          const keyA = a[companyType.value as CompanyTypeKey] ? 1 : 0;
          const keyB = b[companyType.value as CompanyTypeKey] ? 1 : 0;

          if (keyA !== keyB) {
            return keyB - keyA; // Descending (true first)
          }
        }

        // If company types are the same, sort by name (ascending)
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      }),
  },
};

export const groupStrategies: Record<ViewByOption, GroupStrategy> = {
  [VIEW_BY_OPTIONS.NAME]: {
    group: () => [],
    needsGrouping: false,
  },
  [VIEW_BY_OPTIONS.COUNTRY]: {
    group: (companies) => {
      const groups = new Map<
        string,
        { companies: SpogCompany[]; alpha2: string }
      >();
      companies.forEach((company) => {
        const country =
          company.locationPrimary?.state?.country?.name || "Unknown";
        const alpha2 = company.locationPrimary?.state?.country?.alpha2 || "";
        if (!groups.has(country)) {
          groups.set(country, { companies: [], alpha2 });
        }
        groups.get(country)!.companies.push(company);
      });

      return Array.from(groups.entries()).map(
        ([country, { companies, alpha2 }]) => ({
          key: country,
          companies,
          icon: alpha2 ? (
            <CountryLogo alpha2={alpha2} size={20} variant="rounded" />
          ) : (
            <Language sx={{ fontSize: 20 }} />
          ),
        })
      );
    },
    needsGrouping: true,
  },
  [VIEW_BY_OPTIONS.REVENUE]: {
    group: (companies) => {
      const groups = new Map<string, SpogCompany[]>();
      companies.forEach((company) => {
        const revenueMin = company.revenueRange?.min;
        const band =
          typeof revenueMin === "number"
            ? getRevenueBand(revenueMin)
            : "Unknown";

        if (!groups.has(band)) {
          groups.set(band, []);
        }
        groups.get(band)!.push(company);
      });

      // Sort groups by revenue band order (highest first)
      const sortedEntries = Array.from(groups.entries()).sort(
        ([bandA], [bandB]) => {
          // Handle "Unknown" revenue - should go last
          if (bandA === "Unknown") return 1;
          if (bandB === "Unknown") return -1;

          const indexA = REVENUE_BANDS.findIndex(
            (band) => band.label === bandA
          );
          const indexB = REVENUE_BANDS.findIndex(
            (band) => band.label === bandB
          );

          // If not found in REVENUE_BANDS, put at the end
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        }
      );

      return sortedEntries.map(([band, companies]) => ({
        key: band,
        companies,
        icon: <AttachMoney sx={{ fontSize: 20 }} />,
      }));
    },
    needsGrouping: true,
  },
  [VIEW_BY_OPTIONS.COMPANY_TYPE]: {
    group: (companies) => {
      const groups = new Map<string, SpogCompany[]>();
      companies.forEach((company) => {
        const companyType = COMPANY_TYPES.find(
          (type) => company[type.value as CompanyTypeKey]
        );
        const label = companyType?.label || "Other";

        if (!groups.has(label)) {
          groups.set(label, []);
        }
        groups.get(label)!.push(company);
      });

      // Sort groups by company type order (GHQ, GP, DP, Other)
      const sortedEntries = Array.from(groups.entries()).sort(
        ([typeA], [typeB]) => {
          const indexA = COMPANY_TYPES.findIndex(
            (type) => type.label === typeA
          );
          const indexB = COMPANY_TYPES.findIndex(
            (type) => type.label === typeB
          );

          // "Other" goes last
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;

          return indexA - indexB;
        }
      );

      return sortedEntries.map(([type, companies]) => ({
        key: type,
        companies,
        icon: <CorporateFare sx={{ fontSize: 20 }} />,
      }));
    },
    needsGrouping: true,
  },
};
